import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin, Search, Navigation2, X, Clock,
  BookOpen, Smartphone, ChevronLeft, ChevronRight,
  Loader2, AlertCircle, Share2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import config from '../config';

const API = config.API_URL || '/api';

// ─── Quick-access cities ──────────────────────────────────────────────────────
const QUICK_CITIES = [
  { label: 'New York',    lat: 40.7128, lng:  -74.0060 },
  { label: 'Miami',       lat: 25.7617, lng:  -80.1918 },
  { label: 'Los Angeles', lat: 34.0522, lng: -118.2437 },
  { label: 'Baltimore',   lat: 39.2904, lng:  -76.6122 },
  { label: 'Chicago',     lat: 41.8781, lng:  -87.6298 },
  { label: 'Jerusalem',   lat: 31.7683, lng:   35.2137 },
];

// ─── Slug → city name (for SEO URLs like /shabbat/new-york) ─────────────────────
const CITY_SLUG_MAP = {
  'new-york': 'New York, NY',       'brooklyn': 'Brooklyn, NY',
  'los-angeles': 'Los Angeles, CA', 'miami': 'Miami, FL',
  'chicago': 'Chicago, IL',         'jerusalem': 'Jerusalem',
  'tel-aviv': 'Tel Aviv',           'baltimore': 'Baltimore, MD',
  'philadelphia': 'Philadelphia, PA','boston': 'Boston, MA',
  'houston': 'Houston, TX',         'dallas': 'Dallas, TX',
  'atlanta': 'Atlanta, GA',         'denver': 'Denver, CO',
  'seattle': 'Seattle, WA',         'san-francisco': 'San Francisco, CA',
  'las-vegas': 'Las Vegas, NV',     'phoenix': 'Phoenix, AZ',
  'toronto': 'Toronto',             'london': 'London',
  'passaic': 'Passaic, NJ',         'teaneck': 'Teaneck, NJ',
  'monsey': 'Monsey, NY',           'boca-raton': 'Boca Raton, FL',
  'cleveland': 'Cleveland, OH',     'st-louis': 'St. Louis, MO',
  'detroit': 'Detroit, MI',         'pittsburgh': 'Pittsburgh, PA',
  'minneapolis': 'Minneapolis, MN', 'five-towns': 'Five Towns, NY',
  'lakewood': 'Lakewood, NJ',
};

// ─── Formatting helpers ───────────────────────────────────────────────────────

/** "7:23pm" / "19:23" → "7:23 PM" */
function fmt12(raw) {
  if (!raw) return '';
  const s    = String(raw).trim();
  const isPM = /pm/i.test(s);
  const isAM = /am/i.test(s);
  const c    = s.replace(/\s*(am|pm)/gi, '');
  const [hP = '0', mP = '0'] = c.split(':');
  let h = parseInt(hP, 10);
  const m = String(parseInt(mP, 10) || 0).padStart(2, '0');
  if (isPM && h < 12) h += 12;
  if (isAM && h === 12) h = 0;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12    = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${period}`;
}

/** ISO string → "Friday, March 22" — NO timezone shift (dates are local) */
function fmtDateLong(iso) {
  if (!iso) return '';
  try {
    const [y, mo, d] = iso.split('T')[0].split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
    });
  } catch { return ''; }
}

/** ISO string → "Sat, Mar 23" */
function fmtDateShort(iso) {
  if (!iso) return '';
  try {
    const [y, mo, d] = iso.split('T')[0].split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return ''; }
}

/** ms until ISO timestamp → { label, started } */
function countdownTo(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return { label: 'Shabbat Shalom 🕯️', started: true };
  const days    = Math.floor(diff / 86_400_000);
  const hours   = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const parts   = [];
  if (days > 0)    parts.push(`${days}d`);
  if (hours > 0)   parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
  return { label: `In ${parts.join(' ')}`, started: false, days, hours, minutes };
}

/** Format a Nominatim geocoding result into "City, State" */
function fmtSuggestion(s) {
  const addr = s.address || {};
  const city = addr.city || addr.town || addr.village || addr.county || s.name || '';
  const state = addr.state || '';
  const country = (addr.country_code || '').toUpperCase();
  if (city && state) return `${city}, ${state}`;
  if (city && country) return `${city}, ${country}`;
  return s.display_name.split(', ').slice(0, 2).join(', ');
}

// ─── Component ────────────────────────────────────────────────────────────────
const ShabbatTimesPage = () => {
  const navigate = useNavigate();
  const { citySlug } = useParams();
  const [searchParams] = useSearchParams();

  // Location
  const [locLabel,    setLocLabel]    = useState('');
  const [query,       setQuery]       = useState('');
  const [gpsLoading,  setGpsLoading]  = useState(false);
  const [gpsError,    setGpsError]    = useState('');

  // Times data
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // Countdown
  const [countdown, setCountdown] = useState(null);
  const tickRef    = useRef(null);
  const inputRef   = useRef(null);
  const suggestRef = useRef(null);

  // Autocomplete suggestions
  const [suggestions,     setSuggestions]     = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestLoading,  setSuggestLoading]  = useState(false);

  // Share
  const [sharing,     setSharing]     = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // ── Fetch times ───────────────────────────────────────────────────────────
  const fetchTimes = useCallback(async ({ lat, lng, q, label } = {}) => {
    setLoading(true);
    setError('');
    setData(null);
    if (label) setLocLabel(label);

    try {
      const params = new URLSearchParams();
      if (lat != null && lng != null) {
        params.set('lat', lat);
        params.set('lng', lng);
      } else if (q) {
        params.set('q', q);
      } else {
        throw new Error('No location provided');
      }

      const res  = await fetch(`${API}/public/shabbat-times?${params}`);
      const json = await res.json();

      if (!json.success) throw new Error(json.error || 'Could not get times');

      setData(json);
      // Prefer the explicit label from autocomplete selection (precise Nominatim name)
      // Only fall back to server's reverse-geocode city when no label was provided (pure GPS)
      if (label && label !== 'Your location') {
        setLocLabel(label);
      } else if (json.city) {
        setLocLabel([json.city, json.state].filter(Boolean).join(', '));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── GPS detection ─────────────────────────────────────────────────────────
  const detectGps = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('Location not supported by this browser.');
      return;
    }
    setGpsLoading(true);
    setGpsError('');

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        let label = '';

        try {
          const r = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
          );
          if (r.ok) {
            const d = await r.json();
            const city = d.city || d.locality || '';
            const code = d.principalSubdivisionCode || '';
            label = [city, code].filter(Boolean).join(', ');
          }
        } catch { /* non-fatal */ }

        setGpsLoading(false);
        fetchTimes({ lat, lng, label: label || 'Your location' });
      },
      () => {
        setGpsLoading(false);
        setGpsError('Location access denied. Enter a city or zip code below.');
      },
      { timeout: 8000, maximumAge: 300_000 }
    );
  }, [fetchTimes]);

  // Auto-detect GPS on mount — skip if URL already specifies a city
  useEffect(() => {
    const slugCity = citySlug ? (CITY_SLUG_MAP[citySlug] || citySlug.replace(/-/g, ' ')) : null;
    const paramCity = searchParams.get('city') || searchParams.get('q');
    const paramLat  = searchParams.get('lat');
    const paramLng  = searchParams.get('lng');
    if (paramLat && paramLng) {
      fetchTimes({ lat: parseFloat(paramLat), lng: parseFloat(paramLng), label: slugCity || paramCity || '' });
    } else if (slugCity || paramCity) {
      const q = slugCity || paramCity;
      fetchTimes({ q, label: q });
    } else {
      detectGps();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dynamic document.title
  useEffect(() => {
    if (data?.candle_lighting) {
      const city = locLabel || (data.city ? [data.city, data.state].filter(Boolean).join(', ') : '');
      const time = fmt12(data.candle_lighting);
      document.title = city
        ? `Candle Lighting ${city} — ${time} | Yesod`
        : `Shabbat Candle Lighting — ${time} | Yesod`;
    } else {
      document.title = 'Shabbat Candle Lighting Times | Yesod';
    }
    return () => { document.title = 'Yesod'; };
  }, [data, locLabel]);

  // JSON-LD structured data injection
  useEffect(() => {
    const old = document.getElementById('shabbat-jsonld');
    if (old) old.remove();
    if (!data?.candle_lighting) return;
    const city = locLabel || (data.city ? [data.city, data.state].filter(Boolean).join(', ') : '');
    const desc = `Shabbat candle lighting in ${city} is at ${fmt12(data.candle_lighting)}. Havdalah at ${fmt12(data.havdalah)}${data.parsha ? `. Parsha ${data.parsha}` : ''}.`;
    const script = document.createElement('script');
    script.id   = 'shabbat-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: `Shabbat Candle Lighting — ${city}`,
      description: desc,
      startDate: data.candle_iso  || '',
      endDate:   data.havdalah_iso || '',
      location:  { '@type': 'Place', name: city },
      organizer: { '@type': 'Organization', name: 'Yesod', url: 'https://yesod.us' },
    });
    document.head.appendChild(script);
    return () => { const el = document.getElementById('shabbat-jsonld'); if (el) el.remove(); };
  }, [data, locLabel]);

  // ── Countdown ticker ──────────────────────────────────────────────────────
  useEffect(() => {
    clearInterval(tickRef.current);
    if (!data?.candle_iso) return;

    const tick = () => setCountdown(countdownTo(data.candle_iso));
    tick();
    tickRef.current = setInterval(tick, 60_000);
    return () => clearInterval(tickRef.current);
  }, [data?.candle_iso]);

  // ── Address autocomplete (Nominatim, free/no key) ─────────────────────────
  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSuggestLoading(false);
      return;
    }
    setSuggestLoading(true);
    const ctrl  = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query.trim())}&format=json&limit=6&addressdetails=1`,
          { signal: ctrl.signal, headers: { 'Accept-Language': 'en' } }
        );
        const results = await r.json();
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch { /* aborted or network — ignore */ }
      finally { setSuggestLoading(false); }
    }, 350);
    return () => { clearTimeout(timer); ctrl.abort(); setSuggestLoading(false); };
  }, [query]);

  // ── Close suggestions on outside click ───────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Select autocomplete suggestion ───────────────────────────────────────
  const selectSuggestion = (s) => {
    const label = fmtSuggestion(s);
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    fetchTimes({ lat: parseFloat(s.lat), lng: parseFloat(s.lon), label });
    inputRef.current?.blur();
  };

  // ── Chip click — instant switch ────────────────────────────────────────
  const handleChipClick = (city) => {
    setQuery('');
    fetchTimes({ lat: city.lat, lng: city.lng, label: city.label });
    inputRef.current?.blur();
  };

  // ── Open app store ────────────────────────────────────────────────────────
  const openApp = () => {
    const ua = navigator.userAgent || '';
    window.open(
      /android/i.test(ua)
        ? 'https://play.google.com/store/apps/details?id=us.yesod.app'
        : 'https://apps.apple.com/us/app/yesod/id6748472269',
      '_blank'
    );
  };

  // ── Share this candle lighting result ─────────────────────────────────────
  const handleShare = async () => {
    if (!hasData) return;
    setSharing(true);
    try {
      const res = await fetch(`${API}/shabbat-share/create`, {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({
          city           : displayLocation || data.city,
          candle_lighting: candleTime,
          havdalah       : havTime,
          parsha         : data.parsha || null,
          date           : candleDate,
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error('Share failed');
      const shareUrl = json.data.share_url;

      if (navigator.share) {
        await navigator.share({
          title: `Shabbat times — ${displayLocation || data.city}`,
          text : `Candle lighting at ${candleTime}${data.parsha ? ` · ${data.parsha}` : ''}`,
          url  : shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      }
    } catch { /* user cancelled or network error — fail silently */ }
    finally { setSharing(false); }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const hasData         = !!data?.candle_lighting;
  const candleTime      = fmt12(data?.candle_lighting);
  const candleDate      = fmtDateLong(data?.candle_iso);
  const havTime         = fmt12(data?.havdalah);
  const havDate         = fmtDateShort(data?.havdalah_iso);
  const displayLocation = locLabel || (data ? [data.city, data.state].filter(Boolean).join(', ') : '');

  const activeChipLabel = (() => {
    if (!displayLocation) return null;
    const base = displayLocation.split(',')[0].trim().toLowerCase();
    return QUICK_CITIES.find(c => c.label.toLowerCase() === base)?.label ?? null;
  })();

  // ── Candle flicker (Clock) animation ────────────────────────────────────
  const clockSwing = {
    animate   : { rotate: [-12, 12] },
    transition: { duration: 2.2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' },
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col antialiased">

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 h-[52px] flex items-center px-4 gap-3 bg-white/95 backdrop-blur-md border-b border-gray-100 flex-shrink-0">
        <button
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="flex items-center gap-0.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors flex-shrink-0"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
        <h1 className="flex-1 flex items-center justify-center gap-1.5 text-sm font-bold text-gray-900 tracking-tight">
          <Clock className="w-3.5 h-3.5 text-amber-500" />
          Shabbat Times
        </h1>
        <div className="w-[60px]" />
      </nav>

      {/* ── Layout: sidebar (desktop) + content ─────────────────────────── */}
      <div className="flex flex-col lg:flex-row flex-1">

        {/* ── Search panel — always visible ───────────────────────────── */}
        <div className={cn(
          'sticky top-[52px] z-40 bg-white flex-shrink-0',
          'border-b border-gray-100 shadow-[0_1px_4px_rgba(0,0,0,0.05)]',
          'px-4 pb-2 pt-2.5',
          'lg:w-72 lg:min-w-[260px] lg:border-b-0 lg:border-r lg:shadow-none',
          'lg:px-5 lg:py-5 lg:min-h-[calc(100vh-52px)] lg:self-start lg:sticky',
        )}>

          {/* Search row — autocomplete only, no free-text submit */}
          <form onSubmit={e => e.preventDefault()} className="flex items-center gap-2 mb-1.5">
            <div className="relative flex-1" ref={suggestRef}>
              {/* Left icon: spinner while fetching suggestions, magnifier otherwise */}
              {suggestLoading
                ? <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400 animate-spin pointer-events-none" />
                : <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              }
              <input
                ref={inputRef}
                type="text"
                placeholder={displayLocation || 'City, address, or zip code…'}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                autoCapitalize="words"
                autoCorrect="off"
                spellCheck="false"
                className="w-full pl-9 pr-9 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
              />
              {/* Right icon: clear when typing, GPS when empty */}
              {query ? (
                <button
                  type="button"
                  onClick={() => { setQuery(''); setSuggestions([]); setShowSuggestions(false); setSuggestLoading(false); inputRef.current?.focus(); }}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={detectGps}
                  disabled={gpsLoading}
                  aria-label="Use my current location"
                  title="Use my current location"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-indigo-400 hover:text-indigo-600 disabled:opacity-40 transition-colors"
                >
                  {gpsLoading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Navigation2 className="w-4 h-4" />
                  }
                </button>
              )}

              {/* Autocomplete dropdown */}
              <AnimatePresence>
                {showSuggestions && suggestions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.12 }}
                    className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden"
                  >
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onMouseDown={e => { e.preventDefault(); selectSuggestion(s); }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2.5 border-b border-gray-50 last:border-0 transition-colors"
                      >
                        <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700 truncate">{fmtSuggestion(s)}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </form>

          {gpsError && (
            <p className="text-xs text-red-500 mb-1.5 px-0.5">{gpsError}</p>
          )}

          {/* City chips */}
          <div
            role="list"
            aria-label="Quick city selection"
            className="flex gap-1.5 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-0.5 lg:mx-0 lg:px-0 lg:flex-wrap lg:overflow-visible"
          >
            {QUICK_CITIES.map(c => (
              <button
                key={c.label}
                role="listitem"
                onClick={() => handleChipClick(c)}
                aria-pressed={activeChipLabel === c.label}
                className={cn(
                  'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap',
                  activeChipLabel === c.label
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-800'
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content ──────────────────────────────────────────────────── */}
        <div className="flex-1 px-4 py-5 lg:px-10 lg:py-8 max-w-xl mx-auto w-full lg:max-w-lg lg:mx-0">

          {/* Loading */}
          {(loading || (gpsLoading && !hasData)) && (
            <div className="flex flex-col items-center justify-center min-h-[44vh] gap-3">
              <motion.div {...clockSwing}>
                <Clock className="w-12 h-12 text-amber-400" />
              </motion.div>
              <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
              <p className="text-sm text-gray-400">
                {gpsLoading && !loading ? 'Detecting your location…' : `Getting times for ${displayLocation || 'your location'}…`}
              </p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-2xl p-4 mb-4">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Empty state */}
          {!loading && !gpsLoading && !hasData && !error && (
            <div className="flex flex-col items-center justify-center min-h-[44vh] gap-3 text-center">
              <motion.div {...clockSwing} className="mb-1">
                <Clock className="w-12 h-12 text-amber-400" />
              </motion.div>
              <h2 className="text-base font-semibold text-gray-700">Get this week's Shabbat times</h2>
              <p className="text-sm text-gray-400">Search a city above or tap the location button</p>
            </div>
          )}

          {/* ── Result ─────────────────────────────────────────────────── */}
          <AnimatePresence mode="wait">
            {hasData && !loading && (
              <motion.div
                key={displayLocation}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >

                {/* Main card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-3">

                  {/* Amber accent strip */}
                  <div className="h-[3px] bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500" />

                  {/* ── 1. LOCATION — hero element ─────────────────────── */}
                  <div className="px-6 pt-5 pb-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-1.5">
                          <MapPin className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-1.5" />
                          <h2 className="text-[1.875rem] leading-tight font-extrabold text-gray-900 tracking-tight">
                            {displayLocation || data.city}
                          </h2>
                        </div>
                        {/* ── 2. DATE ──────────────────────────────────── */}
                        <p className="text-sm text-gray-400 font-medium mt-1 pl-[22px]">
                          {candleDate}
                        </p>
                      </div>

                      {/* Share button + Clock icon */}
                      <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                        <button
                          onClick={handleShare}
                          disabled={sharing}
                          aria-label="Share candle lighting times"
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors disabled:opacity-50"
                        >
                          {sharing
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Share2 className="w-3.5 h-3.5" />
                          }
                        </button>
                        <motion.div {...clockSwing}>
                          <Clock className="w-6 h-6 text-amber-500" />
                        </motion.div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-50 mx-6" />

                  {/* ── 3. CANDLE LIGHTING ─────────────────────────────── */}
                  <div className="px-6 py-5 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400 mb-1.5">
                        Candle Lighting
                      </p>
                      {countdown && !countdown.started && (
                        <div className="flex items-center gap-1 text-indigo-500">
                          <Clock className="w-3 h-3" />
                          <span className="text-xs font-semibold">{countdown.label}</span>
                        </div>
                      )}
                      {countdown?.started && (
                        <span className="text-xs font-semibold text-amber-600">
                          Shabbat Shalom ✨
                        </span>
                      )}
                    </div>
                    <p className="text-[2.75rem] font-black text-gray-900 tabular-nums tracking-tight leading-none">
                      {candleTime}
                    </p>
                  </div>

                  {/* ── 4. HAVDALAH ────────────────────────────────────── */}
                  {havTime && (
                    <>
                      <div className="border-t border-gray-50 mx-6" />
                      <div className="px-6 py-4 flex items-center justify-between gap-4">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400 mb-1">
                            Havdalah
                          </p>
                          <p className="text-xs text-gray-400 font-medium">{havDate}</p>
                        </div>
                        <p className="text-2xl font-bold text-gray-400 tabular-nums tracking-tight">
                          {havTime}
                        </p>
                      </div>
                    </>
                  )}

                  {/* ── 5. FOOTER: parsha + note ───────────────────────── */}
                  <div className="border-t border-gray-50 bg-gray-50/60 px-6 py-3 flex items-center justify-between flex-wrap gap-2">
                    {data.parsha ? (
                      <div className="flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5 text-indigo-400" />
                        <span className="text-xs font-semibold text-indigo-600">
                          Parshat {data.parsha}
                        </span>
                      </div>
                    ) : <div />}
                    <span className="text-[10px] text-gray-400 font-medium">
                      18 min before sunset · {data.timezone}
                    </span>
                  </div>
                </div>

                {/* App CTA */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={openApp}
                  onKeyDown={e => e.key === 'Enter' && openApp()}
                  className="group flex items-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-4 cursor-pointer hover:border-gray-200 hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Never miss candle lighting</p>
                    <p className="text-xs text-gray-400 mt-0.5">Weekly reminders + your community's events</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                </div>

              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>

      {/* ── Share toast ───────────────────────────────────────────────────── */}
      <AnimatePresence>
        {shareCopied && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.18 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-full shadow-lg z-50 flex items-center gap-2 whitespace-nowrap"
          >
            <span className="text-emerald-400">✓</span>
            Link copied to clipboard
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default ShabbatTimesPage;
